/*
  Bundle integrity -- strips mix & match kit properties from cart lines once the kit
  they belong to has been broken.

  Why: when a customer removes one item of a kit (or changes a kit line's quantity),
  the discount function correctly stops discounting it, but the remaining lines still
  carry `_bundle`, `_bundle_id`, ... The order then shows bundle metadata and the
  "Tag orders with bundle line item property" Flow tags it `bundle`, inflating bundle
  reporting with orders that never contained a bundle.

  A kit is broken when its line count differs from `_bundle_count`, or any of its
  lines has a quantity other than 1 -- the same rules the discount function enforces,
  so a line is stripped exactly when it has already lost its discount. Prices do not
  change.

  How a line is stripped, verified against the live Ajax Cart API (2026-10-05):
  - `/cart/change.js` with `properties` REPLACES the line's properties, so a line that
    also carries other properties is changed in place with the bundle keys left out.
  - `properties: {}` is silently ignored, and blank or null values leave the key on
    the line (the Flow matches on the key). So a line with only kit properties is
    re-added without properties, then the old line is removed. Adding first means a
    failed add (e.g. stock) leaves the customer's item in the cart.

  Only the kit's own keys are touched. The older fixed-bundle system's `_bundle_item`
  lines are not kits and are left alone.
*/
(function () {
  if (window.BundleIntegrity) return;

  const BUNDLE_KEYS = [
    '_bundle',
    '_bundle_id',
    '_bundle_name',
    '_bundle_key',
    '_bundle_count',
    '_bundle_group',
    '_bundle_index',
    '_discount_type',
    '_discount_value',
    '_discount_label',
  ];

  // A broken kit can hold at most 12 lines (the function's maximum); this only stops a
  // cart that refuses to change from looping forever.
  const MAX_OPERATIONS = 30;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  async function request(path, body) {
    const response = await fetch(root() + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) throw new Error(`${path} returned ${response.status}`);
    return response.json();
  }

  function brokenBundleIds(cart) {
    const bundles = new Map();
    cart.items.forEach((item) => {
      const id = item.properties && item.properties._bundle_id;
      if (!id) return;
      const bundle = bundles.get(id) || { claimed: item.properties._bundle_count, lines: 0, quantitiesOk: true };
      bundle.lines += 1;
      if (item.quantity !== 1) bundle.quantitiesOk = false;
      bundles.set(id, bundle);
    });

    const broken = new Set();
    bundles.forEach((bundle, id) => {
      if (String(bundle.lines) !== String(bundle.claimed) || !bundle.quantitiesOk) broken.add(id);
    });
    return broken;
  }

  async function stripLine(item) {
    const remaining = {};
    Object.keys(item.properties).forEach((key) => {
      if (!BUNDLE_KEYS.includes(key)) remaining[key] = item.properties[key];
    });

    if (Object.keys(remaining).length) {
      await request('cart/change.js', { id: item.key, quantity: item.quantity, properties: remaining });
      return;
    }

    const plain = { id: item.variant_id, quantity: item.quantity };
    if (item.selling_plan_allocation) plain.selling_plan = item.selling_plan_allocation.selling_plan.id;
    await request('cart/add.js', { items: [plain] });
    await request('cart/change.js', { id: item.key, quantity: 0 });
  }

  async function run() {
    let cart = await request('cart.js');
    let changed = false;
    const failedKeys = new Set();

    for (let i = 0; i < MAX_OPERATIONS; i++) {
      const broken = brokenBundleIds(cart);
      const item = cart.items.find(
        (line) => line.properties && broken.has(line.properties._bundle_id) && !failedKeys.has(line.key)
      );
      if (!item) break;

      try {
        await stripLine(item);
        changed = true;
      } catch (error) {
        console.error('[bundle-integrity] could not strip bundle properties from', item.key, error);
      }

      cart = await request('cart.js');
      // Read the cart back rather than trusting the 200: if the line is still there
      // with its key unchanged, nothing happened, so don't try it again.
      if (cart.items.some((line) => line.key === item.key)) failedKeys.add(item.key);
    }

    return { changed, cart };
  }

  // Calls made while a repair is running share it, so two cart edits in quick
  // succession can't both re-add the same line.
  let inFlight = null;

  function repair() {
    if (!inFlight) {
      inFlight = run()
        .catch((error) => {
          console.error('[bundle-integrity]', error);
          return { changed: false, cart: null };
        })
        .finally(() => {
          inFlight = null;
        });
    }
    return inFlight;
  }

  // Safety net at checkout, for carts that were broken some other way (or before this
  // shipped). Checkout leaves the page, so re-rendering the cart afterwards isn't needed.
  // Accelerated checkout buttons (Shop Pay, PayPal) bypass this; the Flow's
  // complete-kit check is what covers those.
  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('button[name="checkout"]');
    if (!button || button.dataset.bundleChecked === 'true') return;

    event.preventDefault();
    button.setAttribute('aria-busy', 'true');
    repair().finally(() => {
      button.removeAttribute('aria-busy');
      // The flag lets this re-dispatched click through; clearing it straight after
      // means a customer who comes back with the Back button is checked again.
      button.dataset.bundleChecked = 'true';
      button.click();
      delete button.dataset.bundleChecked;
    });
  }, true);

  window.BundleIntegrity = { repair, brokenBundleIds };
})();
